import * as React from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CreateButtonWithModal from '../components/common/CreateButtonWithModal';
import BlackWhiteSwitch from '../components/common/BlackWhiteSwitch';
import DeleteButton from '../components/common/DeleteButton';
import EditModal from '../components/common/EditModal';

interface Column {
  id: keyof Data | 'action';
  label: string;
  minWidth?: number;
  align?: 'right' | 'center' | 'left';
}

interface Data {
  ID: string;
  name: string;
  services: string;
  contactperson: string;
  contactemail: string;
  contactphone: string;
  'website URL': string;
  'API based URL': string;
  'Support booking': string;
  'Support cancellation': string;
  status: string;
  createdBy: string;
}

const columns: readonly Column[] = [
  { id: 'ID', label: 'ID', minWidth: 60 },
  { id: 'name', label: 'Name', minWidth: 150 },
  { id: 'services', label: 'Services', minWidth: 120 },
  { id: 'contactperson', label: 'Contact Person', minWidth: 150 },
  { id: 'contactemail', label: 'Contact Email', minWidth: 200 },
  { id: 'contactphone', label: 'Contact Phone', minWidth: 150 },
  { id: 'website URL', label: 'Website URL', minWidth: 200 },
  { id: 'API based URL', label: 'API URL', minWidth: 200 },
  { id: 'Support booking', label: 'Supports Booking', minWidth: 150 },
  { id: 'Support cancellation', label: 'Supports Cancellation', minWidth: 180 },
  { id: 'status', label: 'Status', minWidth: 100 },
  { id: 'createdBy', label: 'Created By', minWidth: 120 },
  { id: 'action', label: 'Action', minWidth: 100, align: 'center' },
];

function createData(
  ID: string,
  name: string,
  services: string,
  contactperson: string,
  contactemail: string,
  contactphone: string,
  websiteURL: string,
  apiURL: string,
  supportBooking: string,
  supportCancellation: string,
  status: string,
  createdBy: string
): Data {
  return {
    ID,
    name,
    services,
    contactperson,
    contactemail,
    contactphone,
    'website URL': websiteURL,
    'API based URL': apiURL,
    'Support booking': supportBooking,
    'Support cancellation': supportCancellation,
    status,
    createdBy,
  };
}

const initialRows: Data[] = [
  createData('SUP001', 'Supplier A', 'Flights, Hotels', 'Alice', 'alice@suppliera.com', '+1-123-456-7890', 'https://www.suppliera.com', 'https://api.suppliera.com', 'Yes', 'Yes', 'Active', 'Admin'),
  createData('SUP002', 'Supplier B', 'Transfers', 'Bob', 'bob@supplierb.com', '+44-789-654-3210', 'https://www.supplierb.com', 'https://api.supplierb.com', 'Yes', 'No', 'Inactive', 'John'),
  createData('SUP003', 'Supplier C', 'Activities', 'Charlie', 'charlie@supplierc.com', '+91-98765-43210', 'https://www.supplierc.com', 'https://api.supplierc.com', 'No', 'Yes', 'Active', 'Emma'),
];

const Suppliers = () => {
  const [rows, setRows] = React.useState<Data[]>(initialRows);
  const [filteredRows, setFilteredRows] = React.useState<Data[]>(initialRows);
  const [editOpen, setEditOpen] = React.useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = React.useState<number | null>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterBy, setFilterBy] = React.useState('');

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilterBy(event.target.value);
  };

  const handleSearch = () => {
    if (!filterBy || !searchTerm) {
      setFilteredRows(rows);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = rows.filter((row) =>
      String(row[filterBy as keyof Data]).toLowerCase().includes(lowerSearch)
    );
    setFilteredRows(filtered);
  };

  const handleEditSave = (updatedData: Data) => {
    if (selectedRowIndex === null) return;
    const updatedRows = [...rows];
    updatedRows[selectedRowIndex] = updatedData;
    setRows(updatedRows);
    setFilteredRows(updatedRows);
    setEditOpen(false);
  };

  const handleDeleteRow = (id: string) => {
    const updatedRows = rows.filter((row) => row.ID !== id);
    setRows(updatedRows);
    setFilteredRows(updatedRows);
  };

  const renderStatusButton = (status: string) => (
    <Button
      variant="contained"
      size="small"
      sx={{
        backgroundColor: status === 'Active' ? '#1cad2d' : '#e31a24',
        color: 'white',
        fontWeight: 'bold',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: status === 'Active' ? '#2e7d32' : '#c62828',
        },
      }}
    >
      {status}
    </Button>
  );

  const renderActions = (row: Data, index: number) => (
    <>
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          setSelectedRowIndex(index);
          setEditOpen(true);
        }}
      >
        <EditIcon />
      </IconButton>
      <DeleteButton
        onDelete={() => handleDeleteRow(row.ID)}
        confirmMessage="Do you really want to delete this item?"
      />
    </>
  );

  React.useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  return (
    <>
      <Box className="headingBtnWrap">
      <Typography variant="h1">Suppliers</Typography>
        <CreateButtonWithModal modalTitle="Suppliers">
          <Box className="modalFormWrap">
            {/* Modal form inputs here */}
            <BlackWhiteSwitch />
          </Box>
        </CreateButtonWithModal>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}
        className="searchFilterDropdownWrap"
      >
        <TextField
          fullWidth
          variant="outlined"
          className="searchFilterInput"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 180 }}>
          {/* <InputLabel>Filter By</InputLabel> */}
          <Select
            value={filterBy}
            onChange={handleFilterChange}
            displayEmpty
            className="searchFilterDropdown"
          >
            <MenuItem value=""><em>Select Filter</em></MenuItem>
            <MenuItem value="ID">ID</MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="services">Services</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="createdBy">Created By</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleSearch} className="searchBtn">
          Search
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow hover key={row.ID}>
                    {columns.map((column) => {
                      if (column.id === 'status') {
                        return (
                          <TableCell key={column.id}>
                            {renderStatusButton(row.status)}
                          </TableCell>
                        );
                      }
                      if (column.id === 'action') {
                        return (
                          <TableCell key={column.id} align="center">
                            {renderActions(row, rowIndex)}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={column.id}>
                          {row[column.id as keyof Data]}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {selectedRowIndex !== null && (
        <EditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          initialData={rows[selectedRowIndex]}
          onSave={handleEditSave}
          excludeFields={['action', 'status']}
          readOnlyFields={['createdBy']}
          title="Edit Suppliers"
        />
      )}
    </>
  );
};

export default Suppliers;
